import React from 'react';
import { Link } from 'react-router-dom';
import { HappeningNow } from '../components/HappeningNow';
import { HomeRecapBanner } from '../components/HomeRecapBanner';
import { ASSETS, CHAPTERS, NEWS, CONFERENCE, ABOUT } from '../mock';
import { ArrowRight, Calendar, MapPin, Vote, Megaphone, HandHeart, Star, Car } from 'lucide-react';

const STATS = [
  { icon: Star, value: '7', label: 'Active Chapters' },
  { icon: Vote, value: '4,200+', label: 'Voters Registered \u201926' },
  { icon: HandHeart, value: '4', label: 'States Represented' },
];

export default function Home() {
  return (
    <div className="fade-in-up">
      {/* HERO */}
      <section className="banner-bg section-noise relative overflow-hidden">
        {/* Backdrop: faded square poster artwork */}
        <div
          className="absolute inset-0 opacity-[0.08] bg-no-repeat bg-center bg-cover pointer-events-none"
          style={{ backgroundImage: `url(${ASSETS.regionLogo})`, filter: 'blur(2px)' }}
          aria-hidden
        />
        {/* Radial gold glow behind medallion */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] max-w-[90vw] max-h-[90vw] rounded-full bg-gradient-radial-gold" />
        </div>

        <div className="relative max-w-5xl mx-auto px-4 py-14 md:py-20 text-center">
          <div className="chip mb-5 inline-block">★ Coalition of Black Trade Unionists</div>

            <div className="relative inline-block mb-7">
              <div className="absolute -inset-6 rounded-full bg-gradient-to-br from-gold/30 via-gold/10 to-transparent blur-2xl" aria-hidden />
              <img
                src={ASSETS.roundLogo}
                alt="CBTU Region III emblem featuring Florida, Georgia, North Carolina, and South Carolina"
                className="relative h-64 w-64 md:h-80 md:w-80 lg:h-96 lg:w-96 rounded-full object-contain shadow-[0_0_60px_rgba(226,181,59,0.35)] mx-auto"
              />
            </div>

          <h1 className="font-display text-5xl md:text-7xl leading-[0.95] mb-4">
            <span className="gold-text-fill">CBTU</span> <span className="text-cbtu-cream">REGION</span> <span className="gold-text-fill">III</span>
          </h1>

          <p className="text-cbtu-text/95 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-4">
            Uniting Black trade unionists across <span className="text-gold">Florida</span>, <span className="text-gold">Georgia</span>, <span className="text-gold">North Carolina</span>, and <span className="text-gold">South Carolina</span> in the unending struggle for economic, political, and social justice.
          </p>

          <p className="font-serif-script text-2xl md:text-3xl text-cbtu-cream mb-8">
            “Working People United Can Never Be Defeated.”
          </p>

          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/conference" className="gold-button px-6 py-3 rounded-sm inline-flex items-center gap-2">
              <Calendar size={16} /> 2026 Conference
            </Link>
            <Link to="/chapters" className="ghost-button px-6 py-3 rounded-sm inline-flex items-center gap-2">
              Find your chapter <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <HappeningNow agenda={CONFERENCE.agenda} />
      {CONFERENCE.recap.active && <HomeRecapBanner />}

      {/* COMPLIMENTARY PARKING */}
      <section className="max-w-7xl mx-auto px-4 py-12" data-testid="home-parking-section">
        <div className="rounded-md overflow-hidden border-2 border-gold/70 bg-gradient-to-br from-[#0f1a3a] to-cbtu-black grid md:grid-cols-[1.1fr_0.9fr] items-center">
          <div className="p-8 md:p-12 order-last md:order-none">
            <div className="chip mb-3 inline-flex items-center gap-2 w-fit"><Car size={13} /> CONFERENCE ATTENDEES</div>
            <h2 className="font-display text-4xl md:text-5xl text-cbtu-cream leading-tight">COMPLIMENTARY <span className="gold-text-fill">PARKING</span></h2>
            <p className="text-cbtu-text/90 mt-3 leading-relaxed">Attending the Region III Conference at the DoubleTree Jacksonville Riverfront? Follow the parking flyer and enter the validation code to receive complimentary parking.</p>
            <div className="mt-5 rounded-sm bg-gold text-cbtu-black px-6 py-4 w-fit">
              <div className="font-heading text-[10px] tracking-widest">VALIDATION CODE</div>
              <div className="font-display text-5xl tracking-wider" data-testid="home-parking-code">CBTU28</div>
            </div>
            <Link to="/conference#parking" className="gold-button mt-6 px-6 py-3 rounded-sm inline-flex items-center gap-2" data-testid="home-parking-btn">Parking Instructions <ArrowRight size={16} /></Link>
          </div>
          <Link to="/conference#parking" className="block p-4 md:p-6" aria-label="View complimentary parking instructions">
            <img src={ASSETS.parkingFlyer} alt="Complimentary parking flyer for CBTU Region III conference attendees, validation code CBTU28" className="w-full max-w-md mx-auto rounded-md gold-border" loading="lazy" data-testid="home-parking-flyer" />
          </Link>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-cbtu-navy border-y border-gold/30">
        <div className="max-w-7xl mx-auto px-4 py-8 grid grid-cols-3 gap-6">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <s.icon className="text-gold mx-auto mb-2" size={28} />
              <div className="font-display text-3xl md:text-4xl gold-text-fill">{s.value}</div>
              <div className="text-[11px] tracking-[0.18em] text-cbtu-muted uppercase mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CONFERENCE CTA STRIP */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-14">
          <div className="grid lg:grid-cols-2 gap-8 items-center bg-gradient-to-r from-[#0f1530] to-[#102818] border border-gold/40 rounded-md overflow-hidden">
            <img src={ASSETS.digitalJournalCover} alt="CBTU Region III 2026 Regional Conference, Jacksonville, Oct 29 – Nov 1, 2026" className="w-full h-full object-cover object-top max-h-[520px]" />
            <div className="p-8 md:p-10">
              <div className="chip mb-3">{CONFERENCE.recap.active ? 'THANK YOU FOR JOINING US' : 'HAPPENING THIS WEEK'}</div>
              <h2 className="font-display text-3xl md:text-4xl text-cbtu-cream leading-tight mb-3">
                <span className="gold-text-fill">REGION III</span> REGIONAL CONFERENCE
              </h2>
              <p className="text-cbtu-text/90 mb-4 leading-relaxed">{CONFERENCE.theme}</p>
              <ul className="space-y-2 text-sm text-cbtu-cream mb-6">
                <li className="flex items-center gap-2"><Calendar size={16} className="text-gold" /> {CONFERENCE.dates}</li>
                <li className="flex items-center gap-2"><MapPin size={16} className="text-gold" /> {CONFERENCE.venue} · Jacksonville, FL</li>
              </ul>
              <div className="flex flex-wrap gap-3">
                <Link to="/conference#agenda" className="gold-button px-5 py-2.5 rounded-sm text-sm" data-testid="home-agenda-btn">Agenda &amp; Meeting Rooms</Link>
                <Link to="/conference#parking" className="ghost-button px-5 py-2.5 rounded-sm text-sm">Parking Info</Link>
                <Link to="/digital-journal" className="ghost-button px-5 py-2.5 rounded-sm text-sm">Digital Ad Journal</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="bg-cbtu-navy/60">
        <div className="max-w-5xl mx-auto px-4 py-16 text-center">
          <div className="star-divider mb-6"><Star size={14} fill="currentColor" /></div>
          <h3 className="font-display text-4xl gold-text-fill mb-4">OUR MISSION</h3>
          <p className="text-cbtu-text/90 leading-relaxed text-lg">{ABOUT.mission}</p>
          <div className="mt-6">
            <Link to="/about" className="ghost-button px-5 py-2.5 rounded-sm text-sm inline-flex items-center gap-2">
              Learn About CBTU <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CHAPTERS GRID */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="chip mb-2">SEVEN CHAPTERS</div>
            <h3 className="font-display text-4xl text-cbtu-cream">OUR <span className="gold-text-fill">CHAPTERS</span></h3>
          </div>
          <Link to="/chapters" className="text-gold hover:text-gold-bright text-sm font-heading tracking-widest inline-flex items-center gap-2">
            VIEW ALL <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {CHAPTERS.map((c) => (
            <Link key={c.slug} to={`/chapters/${c.slug}`} className="card-dark p-5 rounded-md block">
              <div className="flex items-start justify-between mb-3">
                <div className="font-display text-2xl text-cbtu-cream leading-tight">{c.name}</div>
                <span className="text-[10px] tracking-widest text-gold/80 mt-1">{c.state}</span>
              </div>
              <div className="text-xs text-cbtu-muted mb-3">{c.city} · Est. {c.founded}</div>
              <p className="text-sm text-cbtu-text/85 line-clamp-3 mb-4">{c.summary}</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gold">Est. {c.founded}</span>
                <span className="font-heading tracking-widest text-cbtu-cream/80">VISIT →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* NEWS */}
      <section className="bg-cbtu-navy/60 border-t border-gold/20">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
            <div>
              <div className="chip mb-2"><Megaphone className="inline mr-1" size={12}/> LATEST</div>
              <h3 className="font-display text-4xl text-cbtu-cream">NEWS &amp; <span className="gold-text-fill">STATEMENTS</span></h3>
            </div>
            <Link to="/news" className="text-gold hover:text-gold-bright text-sm font-heading tracking-widest inline-flex items-center gap-2">
              READ ALL NEWS <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {NEWS.slice(0, 3).map((n) => (
              <article key={n.id} className="card-dark rounded-md overflow-hidden">
                <div className="aspect-[16/10] bg-cbtu-black overflow-hidden relative">
                  {n.img ? (
                    <img src={n.img} alt={n.title} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center ${n.accent === 'green' ? 'bg-gradient-to-br from-[#1f4a28] to-[#0f1530]' : n.accent === 'navy' ? 'bg-gradient-to-br from-[#0f1530] to-[#0a0e1a]' : 'bg-gradient-to-br from-[#2a1f08] to-[#0a0e1a]'}`}>
                      <div className="text-center px-6">
                        <div className="text-4xl mb-2 text-gold">★</div>
                        <div className="font-display gold-text-fill text-xl tracking-wide">CBTU REGION III</div>
                        <div className="font-heading text-xs tracking-widest text-cbtu-cream/70 mt-1">{n.tag.toUpperCase()}</div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="chip">{n.tag}</span>
                    <span className="text-xs text-cbtu-muted">{n.date}</span>
                  </div>
                  <h4 className="font-heading text-lg text-cbtu-cream leading-snug mb-2">{n.title}</h4>
                  <p className="text-sm text-cbtu-text/85 line-clamp-3">{n.excerpt}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* JOIN CTA */}
      <section className="green-banner">
        <div className="max-w-5xl mx-auto px-4 py-12 text-center">
          <h3 className="font-display text-3xl md:text-4xl text-cbtu-cream mb-3">
            JOIN THE <span className="gold-text-fill">MOVEMENT</span>
          </h3>
          <p className="text-cbtu-cream/90 max-w-2xl mx-auto mb-6">
            Add your voice to thousands of Black trade unionists building power across the South. Sign up for action alerts, events, and Region III news.
          </p>
          <Link to="/contact" className="gold-button px-7 py-3 rounded-sm inline-flex items-center gap-2">Get Involved <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
}
